import debug from "debug";
import TextBoxMethods from "./TextBoxMethods";
import textBoxData from "./TextBoxConfig";
import * as THREE from "three";

const log = debug("app:txt");

/**
 * Manages the editing of text boxes in a 3D scene using Three.js and CSS3D.
 * @category Components
 * @subcategory TextBox
 */
export class TextBoxEditor {
  static instance = null;

  /**
   * Constructs the TextBoxEditor instance.
   * @param {Object} experience - The experience object containing the scene and eventHub.
   */
  constructor(experience) {
    this.scene = experience.scene;
    if (TextBoxEditor.instance) return TextBoxEditor.instance;

    this.eventHub = experience.eventHub;
    this.textBoxMethods = new TextBoxMethods();
    this.resizeHandle = null;

    this.cssGroup = null;
    this.previousState = null;
    this.changedState = null;
    this.currentListeners = {};

    this.defaultText = textBoxData.others[0].text;
    this.setEventListeners();
    TextBoxEditor.instance = this;
  }

  /**
   * Returns the singleton instance of the TextBoxEditor.
   * @param {Object} experience - The experience object.
   * @returns {TextBoxEditor} The singleton instance.
   */
  static getInstance(experience) {
    if (!TextBoxEditor.instance) {
      new TextBoxEditor(experience);
    }
    return TextBoxEditor.instance;
  }

  /**
   * Sets up event listeners for various text box actions.
   */
  setEventListeners() {
    this.eventHub.on("editTextBox", (cssGroup, scale) =>
      this.enableEditMode(cssGroup, scale)
    );
    this.eventHub.on("exitEditTextBox", () => this.disableEditMode());

    this.eventHub.on("updateTextBox", (cssGroup, scale) =>
      this.updateTextBoxAppearance(cssGroup, scale)
    );
    this.eventHub.on("selectedTextBoxes", (selectedTextBoxes) =>
      this.updateTextBoxVisualization(selectedTextBoxes)
    );

    this.eventHub.on("updateTextBoxAlpha", (data) =>
      this.updateTextBoxTransparency(data.value)
    );
  }

  enableEditMode(cssGroup, scale) {
    log("enableEditMode", cssGroup, scale)
    this.eventHub.emit("lockEvents", true);
    if (this.cssGroup) {
      this.disableEditMode();
    }

    this.togglePreventDefaultCtrlR(true);
    this.cssGroup = cssGroup;
    this.cssObject = cssGroup.getObjectByName("CSS3DObject");
    const textBoxElement = this.cssObject.element;
    this.cssGroup.userData.angle = this.getNormalizedAngle(this.cssGroup);

    textBoxElement.innerText =
      this.cssGroup.userData.text === this.defaultText
        ? " "
        : this.cssGroup.userData.text;

    textBoxElement.contentEditable = true;
    textBoxElement.style.pointerEvents = "auto"; // Allow interaction
    textBoxElement.style.overflow = "hidden"; // Keep text inside
    textBoxElement.style.whiteSpace = "pre-wrap"; // Maintain wrapping
    textBoxElement.style.wordBreak = "break-word";
    textBoxElement.style.zIndex = "11001";
    // Create and show resize handle
    textBoxElement.focus();
    textBoxElement.contentEditable = true;
    textBoxElement.style.pointerEvents = "auto";
    textBoxElement.style.zIndex = "11000";
    this.rotateTextElement(this.cssGroup, 0);
    this.createResizeHandle(textBoxElement, cssGroup, scale);
    this.applyBorder("editing");
    log("userdata", this.cssGroup.userData);
    this.previousState = JSON.parse(JSON.stringify(this.cssGroup.userData));
    log("oldData", this.previousState);
  }

  updateTextBoxTransparency(alphaValue) {
  if (!this.scene) return;

  this.scene.traverse((object) => {
    if (object.isGroup && object.userData.mode === "textbox") {
      const cssObject = object.getObjectByName("CSS3DObject");
      if (cssObject) {
        cssObject.element.style.opacity = alphaValue; // Apply transparency
        object.userData.alpha = alphaValue; // Store alpha in userData
      }
    }
  });

  log(`Updated text box transparency to ${alphaValue}`);
}

  

  disableEditMode() {
    this.togglePreventDefaultCtrlR(false);
    log("exiting edit mode");
    if (!this.cssObject || !this.cssObject.element) {
      log("cssObject or its element is not defined");
      return; // Safely exit if cssObject or its element is not defined
    }

    this.cssObject.element.contentEditable = false;
    this.cssObject.element.style.pointerEvents = "none";
    this.cssObject.element.style.zIndex = "auto";
    this.applyBorder("default");
    this.resetAllBordersToOriginal();
    this.removeResizeHandle();
    log("this.angle", this.cssGroup.userData.angle);
    this.rotateTextElement(this.cssGroup, this.cssGroup.userData.angle);
    this.changedState = JSON.parse(JSON.stringify(this.cssGroup.userData));

    const objectId = this.cssGroup.userData.objectId;
    this.currentListeners = {};
    this.resizeHandle = null;
    this.cssGroup = null;
    this.cssObject = null;
    this.eventHub.emit("lockEvents", false);
    const previousState = this.previousState;
    const changedState = this.changedState;

    const mode = "textbox";
    this.eventHub.emit("logEvent", {
      eventType: "edit",
      data: { objectId, mode, changedState, previousState },
    });
    log("data:", this.changedState, this.previousState);
    this.eventHub.emit("xmd");
  }

  /**
   * Creates a resize handle for a text box.
   * @param {HTMLElement} textBoxElement - The text box element.
   * @param {THREE.Group} cssGroup - The group containing the text box.
   * @param {number} scale - The scale factor for the text box.
   */
  createResizeHandle(textBoxElement, cssGroup, scale) {
    // Create the transparent div for resize interaction
    const resizeHandle = document.createElement("div");
    resizeHandle.className = "resize-handle";
    resizeHandle.style.position = "absolute";
    resizeHandle.style.width = "20px"; // Transparent div size for easier interaction
    resizeHandle.style.height = "20px";
    resizeHandle.style.backgroundColor = "transparent"; // Fully transparent
    resizeHandle.style.cursor = "grab"; // Show resize cursor
    resizeHandle.style.right = "0";
    resizeHandle.style.bottom = "0";
    resizeHandle.style.zIndex = "11001"; // High z-index to stay on top of the text box
    this.cssObject.element.style.userSelect = "none";

    // Create the SVG triangle symbol for the corner
    const svgTriangle = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    svgTriangle.setAttribute("id", "resize-triangle"); // Assign an ID to the SVG
    svgTriangle.setAttribute("width", "20"); // Size of the SVG
    svgTriangle.setAttribute("height", "20");
    svgTriangle.setAttribute("viewBox", "0 0 20 20");
    svgTriangle.style.position = "absolute";
    svgTriangle.style.right = "0";
    svgTriangle.style.bottom = "0";
    svgTriangle.style.pointerEvents = "none"; // Prevent SVG from blocking interaction with the handle

    // Create the triangle path inside the SVG
    const trianglePath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path"
    );
    trianglePath.setAttribute("d", "M0,20 L20,20 L20,0 Z"); // Coordinates for the triangle
    trianglePath.setAttribute("fill", "blue"); // Fill color for the triangle

    // Append the triangle path to the SVG
    svgTriangle.appendChild(trianglePath);

    // Append the SVG to the transparent div
    resizeHandle.appendChild(svgTriangle);

    // Append the resize handle to the text box element
    textBoxElement.appendChild(resizeHandle);

    this.resizeHandle = resizeHandle;
    this.addResizeEventListeners(textBoxElement, cssGroup, scale);
    this.captureUserInput(textBoxElement, scale);
  }

  // Remove any existing resize handles by class name
  /**
   * Removes the resize handle from the text box.
   */
  removeResizeHandle() {
    const handles = document.querySelectorAll(".resize-handle");
    this.cssObject.element.style.userSelect = "auto";
    handles.forEach((handle) => handle.remove());

    log(
      "All resize handles removed:",
      document.querySelectorAll(".resize-handle").length === 0
    );
    this.resizeHandle = null;
    this.removeDocumentListeners();
  }
  /**
   * Adds resize event listeners to the resize handle for the text box.
   * @param {HTMLElement} textBoxElement - The text box element.
   * @param {THREE.Group} cssGroup - The group containing the text box.
   * @param {number} scale - The scale factor for the text box.
   */

  addResizeEventListeners(textBoxElement, cssGroup, scale) {
    let startX, startY, startWidth, startHeight;
    log("cssgroup", cssGroup);

    const mouseMoveHandler = (event) => {
      // Calculate the drag vector
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      // Get the angle of rotation in radians and negate it
      const angle = -cssGroup.rotation.y;

      // Rotate the drag vector to match the box's orientation
      const rotatedDx = dx * Math.cos(angle) - dy * Math.sin(angle);
      const rotatedDy = dx * Math.sin(angle) + dy * Math.cos(angle);

      // Apply the rotated drag to the width and height
      const newWidth = Math.max(startWidth + rotatedDx, 48);
      const newHeight = Math.max(startHeight + rotatedDy, 48);

      // Set the new size and update the textbox
      textBoxElement.style.width = `${newWidth}px`;
      textBoxElement.style.height = `${newHeight}px`;

      this.updateTextBoxSize({
        newWidth,
        newHeight,
        cssGroup,
        scale,
      });
    };

    const mouseUpHandler = () => {
      this.removeDocumentListeners();
    };

    this.resizeHandle.addEventListener("mousedown", (event) => {
      event.stopPropagation(); // Prevent further propagation
      startX = event.clientX;
      startY = event.clientY;
      startWidth = parseInt(
        window.getComputedStyle(this.cssObject.element).width,
        10
      );
      startHeight = parseInt(
        window.getComputedStyle(this.cssObject.element).height,
        10
      );

      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", mouseUpHandler);

      this.currentListeners["mousemove"] = mouseMoveHandler;
      this.currentListeners["mouseup"] = mouseUpHandler;
    });
  }

  /**
   * Removes all event listeners added to the document during resizing.
   */
  removeDocumentListeners() {
    Object.entries(this.currentListeners).forEach(([event, handler]) => {
      document.removeEventListener(event, handler);
    });
    this.currentListeners = {}; // Reset listeners tracking
  }

  /**
   * Captures user input changes in the text box element.
   * @param {HTMLElement} textBoxElement - The text box element.
   * @param {number} scale - The scale factor for the text box.
   */
  captureUserInput(textBoxElement, scale) {
    // Handler for input events: box can only grow with content, never shrink
    const inputHandler = () => {
      const currentWidth = this.cssGroup.userData.w * scale;
      const currentHeight = this.cssGroup.userData.h * scale;
      const newWidth = Math.max(currentWidth, textBoxElement.scrollWidth);
      const newHeight = Math.max(currentHeight, textBoxElement.scrollHeight);
      if (newWidth > currentWidth || newHeight > currentHeight) {
        textBoxElement.style.width = `${newWidth}px`;
        textBoxElement.style.height = `${newHeight}px`;
        this.updateTextBoxSize({
          newWidth,
          newHeight,
          cssGroup: this.cssGroup,
          scale,
        });
      }
    };

    // Handler for when the user leaves the textbox
    const blurHandler = () => {
      this.cssGroup.userData.text =
        this.extractTextWithLineBreaks(textBoxElement);
      this.eventHub.emit("xmd"); // Emit any necessary events on blur
      log("blurHandler activated", this.cssGroup.userData.text);
    };

    // Attach both event listeners
    textBoxElement.addEventListener("input", inputHandler);
    textBoxElement.addEventListener("blur", blurHandler);
    textBoxElement.addEventListener("mouseleave", blurHandler);

    // Track both listeners for proper removal later
    this.currentListeners["input"] = inputHandler;
    this.currentListeners["blur"] = blurHandler;
  }

  /**
   * Extracts plain text with line breaks from an HTML element.
   * @param {HTMLElement} element - The HTML element to extract text from.
   * @returns {string} The extracted text with line breaks.
   */
  extractTextWithLineBreaks(element) {
    return element.innerHTML
      .replace(/<div>/g, "\n") // Replace opening divs with new lines
      .replace(/<br>/g, "\n") // Replace breaks with new lines
      .replace(/<\/div>/g, "") // Remove closing divs
      .replace(/<[^>]+>/g, ""); // Remove any other HTML tags
  }

  /**
   * Updates the appearance of the text box based on user data.
   * @param {Object} userData - The user data containing appearance properties.
   * @param {number} scale - The scale factor for the text box.
   */
  updateTextBoxAppearance(userData, scale) {
    log("incoming user data", userData, scale);
    const textBox = this.cssObject.element;
    this.textBoxMethods.applyStyles(textBox, userData, scale);
  }

  /**
   * Updates the size of the text box and its Three.js representation.
   * @param {Object} sizeObject - The size object containing new dimensions and metadata.
   * @param {THREE.Group} sizeObject.cssGroup - The group containing the text box.
   * @param {number} sizeObject.scale - The scale factor for the text box.
   * @param {number} sizeObject.newWidth - The new width of the text box.
   * @param {number} sizeObject.newHeight - The new height of the text box.
   */
  updateTextBoxSize(sizeObject) {
    const { cssGroup, scale, newWidth, newHeight } = sizeObject;

    // Retrieve current width and height from userData
    const currentWidth = cssGroup.userData.w * scale;
    const currentHeight = cssGroup.userData.h * scale;

    // Calculate deltas to maintain top-left corner position
    const deltaX = (newWidth - currentWidth) / 2;
    const deltaY = (newHeight - currentHeight) / 2;

    // Adjust the position of the cssGroup to keep the top-left corner fixed
    cssGroup.position.x += deltaX / scale; // Scale delta to match scene units
    cssGroup.position.z += deltaY / scale; // Assuming z is the vertical direction in 3D space

    // Update the WebGL textBoxMesh
    const textBoxMesh = cssGroup.getObjectByName("textBoxMesh");
    if (textBoxMesh) {
      cssGroup.remove(textBoxMesh);
      textBoxMesh.geometry.dispose();
      textBoxMesh.material.dispose();

      const updatedTextBoxMesh = this.textBoxMethods.createTextBoxMesh(
        newWidth / scale,
        newHeight / scale
      );
      cssGroup.add(updatedTextBoxMesh);
    }

    // Update userData with the new dimensions
    cssGroup.userData.w = newWidth / scale;
    cssGroup.userData.h = newHeight / scale;

    // Log the resize action
    log("Updated TextBox Size:", { newWidth, newHeight, deltaX, deltaY });
  }

  /**
   * Rotates a text element by a specified angle.
   * @param {THREE.Group} cssGroup - The group containing the text box.
   * @param {number} angle - The angle to rotate in degrees.
   */
  rotateTextElement(cssGroup, angle) {
    cssGroup.rotation.y = THREE.MathUtils.degToRad(angle); // Apply rotation around the Z-axis in radians
  }

  /**
   * Converts plain text into HTML with line breaks wrapped in <div>.
   * @param {string} text - The plain text to convert.
   * @returns {string} The converted HTML string.
   */
  convertPlainTextToHtml(text) {
    return text
      .split("\n") // Split by newline
      .map((line) => (line.trim() === "" ? "<br>" : `<div>${line}</div>`)) // Convert each line to <div> or <br>
      .join(""); // Join into a single string
  }

  /**
   * Updates the visualization of selected text boxes.
   * @param {Array<THREE.Group>} selectedTextBoxes - The array of selected text box groups.
   */
  updateTextBoxVisualization(selectedTextBoxes) {
    log("selected tecrtboxes", selectedTextBoxes);
    this.resetAllBordersToOriginal();
    this.applyBorder("selected", selectedTextBoxes);
  }

  /**
   * Applies a border style to a text box or group of text boxes.
   * @param {string} type - The type of border to apply ("selected", "default", or "editing").
   * @param {Array<THREE.Group>} [cssGroup] - Optional group of text boxes for "selected" type.
   */
  applyBorder(type, cssGroup) {
    // Handle no selection case
    if (type === "selected" && cssGroup && cssGroup.length === 0) {
      this.resetAllBordersToOriginal();
      return;
    }

    // Handle selected mode
    if (type === "selected" && cssGroup && cssGroup.length > 0) {
      cssGroup.forEach((group) => {
        const cssObject = group.getObjectByName("CSS3DObject");
        if (cssObject) cssObject.element.style.border = "2px solid green";
      });
      return;
    }

    // Handle default mode
    if (type === "default" && this.cssGroup) {
      const cssObject = this.cssGroup.getObjectByName("CSS3DObject");
      if (cssObject) {
        cssObject.element.style.border =
          this.cssGroup.userData.outlineColor || "none";
      }
      return;
    }

    // Handle editing mode
    if (type === "editing" && this.cssGroup) {
      const cssObject = this.cssGroup.getObjectByName("CSS3DObject");
      if (cssObject) cssObject.element.style.border = "2px solid blue";
    }
  }

  /**
   * Applies a border style to a text box or group of text boxes.
   * @param {string} type - The type of border to apply ("selected", "default", or "editing").
   * @param {Array<THREE.Group>} [cssGroup] - Optional group of text boxes for "selected" type.
   */
  resetAllBordersToOriginal() {
    if (!this.scene) {
      return;
    }

    log("resetting all colors to original color");
    this.scene.traverse((object) => {
      if (object.isGroup && object.userData.mode === "textbox") {
        const cssObject = object.getObjectByName("CSS3DObject");
        if (cssObject) {
          const color = object.userData.outlineColor;
          cssObject.element.style.border = color
            ? `2px solid rgb(${color.red}, ${color.green}, ${color.blue})` // Corrected the string template
            : "none"; // Fallback in case color is not defined
        }
      }
    });
  }

  /**
   * Toggles prevention of the Ctrl+R keyboard shortcut.
   * @param {boolean} enable - Whether to enable or disable the prevention.
   */
  togglePreventDefaultCtrlR(enable) {
    const handler = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r") {
        event.preventDefault();
      }
    };

    if (enable) {
      document.addEventListener("keydown", handler);
      this.ctrlRHandler = handler; // Store for later removal
    } else {
      document.removeEventListener("keydown", this.ctrlRHandler);
    }
  }
  /**
   * Normalizes the angle of a text box to a multiple of 90 degrees.
   * @param {THREE.Group} object - The group containing the text box.
   * @returns {number} The normalized angle in degrees.
   */
  getNormalizedAngle(object) {
    const worldQuaternion = new THREE.Quaternion();
    object.getWorldQuaternion(worldQuaternion);
    const worldRotation = new THREE.Euler().setFromQuaternion(
      worldQuaternion,
      "YZX"
    );
    const worldRotationDegrees =
      Math.round(THREE.MathUtils.radToDeg(worldRotation.y) / 90) * 90;
    return (worldRotationDegrees + 360) % 360;
  }
}
